import React from "react";
import {
  CheckCircleOutlined,
  InboxOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Divider, List, Select, Space, Typography, Upload, message } from "antd";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSupplierInvite, supplierUploadDocument } from "../api";
import type { DocumentCategory, SupplierInvitePublic } from "./types";
import { PlotMapEditor } from "./components/PlotMapEditor";
import { EudrTermHint } from "./components/EudrTermHint";

export function SupplierPublicPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [invite, setInvite] = React.useState<SupplierInvitePublic | null>(null);
  const [category, setCategory] = React.useState<DocumentCategory>("supplier_declaration");
  const [loading, setLoading] = React.useState(true);
  const [mapSaving, setMapSaving] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setInvite(await getSupplierInvite(token));
    } catch {
      message.error(t("supplier.inviteNotFound"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  React.useEffect(() => {
    if (!invite?.missing_categories?.length) return;
    if (!invite.missing_categories.includes(category)) {
      setCategory(invite.missing_categories[0] as DocumentCategory);
    }
  }, [invite, category]);

  if (!token) return null;

  const categoryLabel = (cat: DocumentCategory) =>
    t(`compliance.documents.categories.${cat}`, {
      defaultValue: t(`compliance.documentCategories.${cat}`, { defaultValue: cat }),
    });

  const uploadFile = async (cat: DocumentCategory, file: File) => {
    await supplierUploadDocument(token, cat, file);
    message.success(t("supplier.uploaded"));
    await reload();
  };

  const missing = invite?.missing_categories ?? [];
  const done = missing.length === 0 && Boolean(invite);

  return (
    <div className="supplier-invite-page" data-testid="supplier-invite-page">
      <Space align="center" size="small" style={{ marginBottom: 8 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("supplier.title")}
        </Typography.Title>
        <EudrTermHint term="dds" testId="supplier-dds-hint" />
      </Space>
      <Typography.Paragraph type="secondary" data-testid="supplier-intro">
        {t("supplier.intro")}
      </Typography.Paragraph>
      <Card loading={loading}>
        {invite && (
          <>
            <Alert
              type={done ? "success" : "info"}
              showIcon
              icon={done ? <CheckCircleOutlined /> : <InboxOutlined />}
              message={
                done ? t("supplier.allUploaded") : t("supplier.nextAction")
              }
              description={
                <span>
                  <strong>{invite.supplier_name}</strong> — {invite.commodity.replace("_", " ")}
                  {invite.contract_reference && ` (${invite.contract_reference})`}
                </span>
              }
              style={{ marginBottom: 16 }}
            />

            {missing.length > 0 && (
              <>
                <Typography.Text strong>{t("supplier.checklistTitle")}</Typography.Text>
                <List
                  size="small"
                  data-testid="supplier-category-checklist"
                  style={{ marginTop: 8, marginBottom: 16 }}
                  dataSource={missing}
                  renderItem={(cat) => {
                    const docCat = cat as DocumentCategory;
                    return (
                      <List.Item>
                        <Space>
                          <InboxOutlined style={{ color: "rgba(0,0,0,0.45)" }} />
                          <span>{categoryLabel(docCat)}</span>
                          {docCat === "geolocation" ? <EudrTermHint term="geolocation" /> : null}
                        </Space>
                      </List.Item>
                    );
                  }}
                />
                <Select
                  style={{ width: "100%", marginBottom: 16 }}
                  value={category}
                  onChange={setCategory}
                  options={missing.map((c) => ({
                    value: c as DocumentCategory,
                    label: categoryLabel(c as DocumentCategory),
                  }))}
                />
                <Upload
                  maxCount={1}
                  beforeUpload={(file) => {
                    uploadFile(category, file as unknown as File).catch(() =>
                      message.error(t("supplier.uploadFailed")),
                    );
                    return false;
                  }}
                >
                  <Button type="primary" icon={<UploadOutlined />} data-testid="supplier-upload-cta">
                    {t("supplier.uploadDocument")}
                  </Button>
                </Upload>
              </>
            )}

            <Divider orientation="left">{t("supplier.mapDrawTitle")}</Divider>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              {t("supplier.mapHint")}
            </Typography.Paragraph>
            <PlotMapEditor
              saving={mapSaving}
              onSave={async (file) => {
                setMapSaving(true);
                try {
                  await uploadFile("geolocation", file);
                } catch {
                  message.error(t("supplier.uploadFailed"));
                  throw new Error("upload failed");
                } finally {
                  setMapSaving(false);
                }
              }}
            />
          </>
        )}
      </Card>
    </div>
  );
}
